import { useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Share2, Copy, Check, X } from 'lucide-react';

/**
 * ShareMenu — shows explicit social share buttons (works on both desktop and mobile).
 *
 * Props:
 *   url: string — the URL to share
 *   title: string — the share title/text
 *   compact?: boolean — show just icons (no labels)
 *   onClose?: () => void — optional close callback
 */
export default function ShareMenu({ url, title, compact = false, onClose }) {
  const [copied, setCopied] = useState(false);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const openShare = (shareUrl) => {
    window.open(shareUrl, '_blank', 'width=600,height=500,noopener');
  };

  const platforms = [
    {
      name: 'Facebook',
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      color: 'bg-[#1877F2] hover:bg-[#166FE5] text-white',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      name: 'X',
      icon: (
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      color: 'bg-black hover:bg-gray-800 text-white',
      url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    },
    {
      name: 'LinkedIn',
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
      color: 'bg-[#0A66C2] hover:bg-[#004182] text-white',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {platforms.map((p) => (
        <Button
          key={p.name}
          size="sm"
          className={`rounded-full ${p.color} ${compact ? 'w-8 h-8 p-0' : 'text-xs px-3 h-8'}`}
          onClick={() => openShare(p.url)}
          title={`Share on ${p.name}`}
        >
          {p.icon}
          {!compact && <span className="ml-1.5">{p.name}</span>}
        </Button>
      ))}
      <Button
        size="sm"
        variant="outline"
        className={`rounded-full ${compact ? 'w-8 h-8 p-0' : 'text-xs px-3 h-8'}`}
        onClick={copyLink}
        title="Copy link"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
        {!compact && <span className="ml-1.5">{copied ? 'Copied!' : 'Copy Link'}</span>}
      </Button>
    </div>
  );
}
